/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      //this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMpn();
      this.updateReturnException();
      this.upateSubtitle();
      this.updateInventoryStatus();
      this.updateBarcode();
      this.updateVariantDescription();
      this.updateOpenBoxLinks();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      const availability = this.data.formatted[this.variant.id].availability;
      const inStore = (availability === 'in-store') ? true : false;

      this.addBtn.classList.remove('in-store');
      if (inStore) {
        this.addBtn.classList.add('in-store');
      }
      this.addBtn.disabled = !variantAvailable || inStore;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      let hidePrice = false;
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        const savingsBandgeEl = this.price.querySelector('.price__savings-badge');
        const comparePrice = this.data.formatted[variant.id].compareAtPrice;
        hidePrice = this.data.formatted[variant.id].hidePrice;

        if (this.data.formatted[variant.id].price == '$0.00') {
          hidePrice = true;
        }
        
        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        priceWasEl.innerHTML = '';
        if (priceWasEl && comparePrice) {
          priceWasEl.innerHTML = `Was ${this.data.formatted[variant.id].compareAtPrice}`;
        }

        if (savingsBandgeEl) {
          savingsBandgeEl.innerHTML = 'Save ' + this.data.formatted[variant.id].savingsPrice || '';
          savingsBandgeEl.hidden = hidePrice;
        }

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      this.price.querySelector('.price__default').hidden = hidePrice
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.skus = this.skus || this.section.querySelectorAll('.product-sku__value');
      if (!this.skus) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.skus.forEach(sku => {
        sku.textContent = skuAvailable ? this.variant.sku : '';
        sku.parentNode.hidden = !skuAvailable;
      });
    }

    /**
     * Updates the MPN.
     */
    updateMpn() {
      this.mpns = this.mpns || this.section.querySelectorAll('.product-mpn__value');
      if (!this.mpns) return;

      const mpnAvailable = this.variant && this.data.formatted[this.variant.id].mpn;
      this.mpns.forEach(mpn => {
        mpn.textContent = mpnAvailable ? this.data.formatted[this.variant.id].mpn : '';
        mpn.parentNode.hidden = !mpnAvailable;
      });
    }
    
    /**
     * Updates the Return Exception.
     */
    updateReturnException() {
      this.returnExceptions = this.returnExceptions || this.section.querySelectorAll('.product-return-exception');
      if (!this.returnExceptions) return;

      this.returnExceptions.forEach(returnException => {
        returnException.textContent = this.data.formatted[this.variant.id].returnException || '';
      });
    }

    updateVariantDescription() {
      this.variantDescriptions = this.variantDescriptions || this.section.querySelectorAll('.variant-description');
      if (!this.variantDescriptions) return;

      this.variantDescriptions.forEach(description => {
        description.textContent = this.data.formatted[this.variant.id].description || '';
      })
    }
    
    upateSubtitle() {
      this.subtitles = this.subtitles || this.section.querySelectorAll('.product-subtitle');
      if (!this.subtitles) return;

      this.subtitles.forEach(subtitle => {
          subtitle.textContent = this.data.formatted[this.variant.id].subtitle;
      })
    }
    
    updateInventoryStatus() {
      this.statuses = this.statuses || this.section.querySelectorAll('.product-inventory-status');
      if (!this.statuses) return;

      let statusContent = this.data.formatted[this.variant.id].inventoryStatus;
      if (this.data.formatted[this.variant.id].inventory_quantity < 1) {
        statusContent = this.data.formatted[this.variant.id].inventoryStatusOOS;
      }

      this.statuses.forEach(status => {
        let statusTextEl = status.querySelector('.product-inventory-text');
        let detailsOpener = status.querySelector('modal-opener');

        detailsOpener.setAttribute('data-modal', `modal-inventory-${this.variant.id}`);

        statusTextEl.innerHTML = statusContent;
        statusTextEl.hidden = !statusContent.length;
        detailsOpener.hidden = !this.data.formatted[this.variant.id].inventoryDetailsPage.length;
      });

      this.updateReturnsPopup(statusContent);
    }

    updateReturnsPopup(statusContent) {
      const popupOpeners = this.section.querySelectorAll('.popup-opener');
      
      popupOpeners.forEach(popupOpener => {
        const button = popupOpener.querySelector('button');
        const link = popupOpener.querySelector('a');
        
        const buttonText = button ? button.textContent.trim() : '';
        const linkText = link ? link.textContent.trim() : '';
        
        const isReturnsPopup = buttonText.toLowerCase().includes('30-day returns') ||
                              linkText.toLowerCase().includes('30-day returns');
        
        if (isReturnsPopup) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = statusContent || '';
          const statusText = tempDiv.textContent || tempDiv.innerText || '';
          const shouldHide = statusText.toLowerCase().trim().startsWith('special order');
          popupOpener.style.display = shouldHide ? 'none' : '';

        }
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the open box links.
     */
    updateOpenBoxLinks() {
      this.openBox = this.openBox || this.section.querySelector('.open-box-products');
      if (!this.openBox) return;

      const gradeLetters  = ['a', 'b', 'c'];
      let openBoxHtml = '';

      gradeLetters.forEach((gradeLetter) => {
        let grade = 'openBox' + gradeLetter.toUpperCase();
        let openBoxProduct = this.data.formatted[this.variant.id][grade];
        
        if (openBoxProduct && openBoxProduct.available) {
          openBoxHtml += `<div class="open-box-product">`
          openBoxHtml += `${gradeLetter.toUpperCase()}  Grade Open Box <a class="open-box-product__link" href="${openBoxProduct.url}">From ${openBoxProduct.price}</a>`
          openBoxHtml += `</div>`
        }
      })
      this.openBox.innerHTML = openBoxHtml;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
